/**
 * FAQ Page Search
 * Provides search, filtering, and highlighting across all FAQ categories.
 */
(function () {
  'use strict';

  /**
   * @param {string} containerSelector
   */
  function initFaqPageSearch(containerSelector) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const searchInput = container.querySelector('.faq-page__search-input');
    const clearButton = container.querySelector('.faq-page__search-clear');
    const categories = container.querySelectorAll('[data-category]');
    const noResults = container.querySelector('.faq-page__no-results');

    if (!searchInput || !categories.length) return;

    const MIN_SEARCH_LENGTH = 3;

    /**
     * @param {HTMLElement} element
     * @returns {string}
     */
    function getSearchableText(element) {
      const summary = element.querySelector('summary');
      const content = element.querySelector('.details-content');
      let text = '';

      if (summary) {
        const clone = /** @type {HTMLElement} */ (summary.cloneNode(true));
        clone.querySelectorAll('.svg-wrapper').forEach(function (el) {
          el.remove();
        });
        text += (clone.textContent || '').trim() + ' ';
      }
      if (content) {
        text += (content.textContent || '').trim();
      }
      return text.toLowerCase();
    }

    /**
     * @param {HTMLElement} element
     */
    function removeHighlights(element) {
      element.querySelectorAll('.faq-page__search-highlight').forEach(function (mark) {
        var parent = mark.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
          parent.normalize();
        }
      });
    }

    /**
     * @param {HTMLElement} element
     * @param {string} term
     */
    function highlightText(element, term) {
      if (!term) {
        removeHighlights(element);
        return;
      }

      var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          var p = node.parentElement;
          while (p && p !== element) {
            if (
              p.classList.contains('svg-wrapper') ||
              p.classList.contains('faq-page__search-highlight')
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            p = p.parentElement;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      var nodes = [];
      var n;
      while ((n = walker.nextNode())) nodes.push(n);

      var escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var regex = new RegExp('(' + escaped + ')', 'gi');

      nodes.forEach(function (textNode) {
        var text = textNode.textContent;
        if (!text || !regex.test(text)) return;

        regex.lastIndex = 0;
        var html = text.replace(regex, '<mark class="faq-page__search-highlight">$1</mark>');
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var frag = document.createDocumentFragment();
        while (tmp.firstChild) frag.appendChild(tmp.firstChild);
        if (textNode.parentNode) textNode.parentNode.replaceChild(frag, textNode);
      });
    }

    /**
     * @param {string} searchTerm
     */
    function filterFaqs(searchTerm) {
      var term = searchTerm.toLowerCase().trim();
      var shouldFilter = term === '' || term.length >= MIN_SEARCH_LENGTH;
      var totalVisible = 0;

      categories.forEach(function (cat) {
        var items = cat.querySelectorAll('[data-faq-item]');
        var categoryVisible = 0;

        items.forEach(function (item) {
          var summary = item.querySelector('summary');
          var content = item.querySelector('.details-content');
          var details = item.querySelector('details');

          if (summary) removeHighlights(/** @type {HTMLElement} */ (summary));
          if (content) removeHighlights(/** @type {HTMLElement} */ (content));

          if (!shouldFilter) {
            item.removeAttribute('data-hidden');
            categoryVisible++;
            return;
          }

          var text = getSearchableText(/** @type {HTMLElement} */ (item));
          var matches = term === '' || text.includes(term);

          if (matches) {
            item.removeAttribute('data-hidden');
            categoryVisible++;

            if (term !== '') {
              if (summary) highlightText(/** @type {HTMLElement} */ (summary), searchTerm);
              if (content) highlightText(/** @type {HTMLElement} */ (content), searchTerm);
              if (details && !details.open) details.open = true;
            } else {
              if (details && details.open) details.open = false;
            }
          } else {
            item.setAttribute('data-hidden', 'true');
          }
        });

        if (shouldFilter && categoryVisible === 0 && term !== '') {
          cat.setAttribute('data-hidden', 'true');
        } else {
          cat.removeAttribute('data-hidden');
        }

        totalVisible += categoryVisible;
      });

      if (noResults) {
        if (shouldFilter && term !== '' && totalVisible === 0) {
          noResults.classList.remove('visually-hidden');
        } else {
          noResults.classList.add('visually-hidden');
        }
      }
    }

    function updateClearButton() {
      var input = /** @type {HTMLInputElement} */ (searchInput);
      if (clearButton) {
        if (input.value.trim() !== '') {
          clearButton.classList.remove('visually-hidden');
        } else {
          clearButton.classList.add('visually-hidden');
        }
      }
    }

    searchInput.addEventListener('input', function (e) {
      var target = /** @type {HTMLInputElement} */ (e.target);
      filterFaqs(target.value);
      updateClearButton();
    });

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        var input = /** @type {HTMLInputElement} */ (searchInput);
        input.value = '';
        filterFaqs('');
        updateClearButton();
        input.focus();
      });
    }

    updateClearButton();
  }

  // @ts-ignore
  window.initFaqPageSearch = initFaqPageSearch;
})();
